import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { backendServices, useCurrentCompanyId, useCurrentUser, useUserHasPermission } from '../services';
import { DashboardMenu, DashboardPortalType, DashboardSubMenu } from '../types/Enums';
import { parseGraphConfiguration } from '../features/DynamicDashboard/DashboardGenericReport/Utils';

const dashboardMenuOrder = [
    DashboardMenu.Overview,
    DashboardMenu.StraightThroughProcessing,
    DashboardMenu.Processing,
    DashboardMenu.Invoices,
    DashboardMenu.PurchaseOrders,
    DashboardMenu.POAcknowledgements,
    DashboardMenu.ASNs,
    DashboardMenu.Suppliers,
    DashboardMenu.Spend,
];

export interface StructuredDashboardReportMetadata {
    [menu: number]: {
        [subMenu: string]: backendServices.ViewModels.DashboardReportMetadataViewModel[];
    };
}

export interface UseGetDashboardReportMetadataOptions {
    enabled?: boolean;
    portal?: DashboardPortalType;
}

export const useGetDashboardReportMetadata = ({
    enabled = true,
    portal,
}: UseGetDashboardReportMetadataOptions = {}) => {
    const companyId = useCurrentCompanyId();
    const user = useCurrentUser('mustBeLoggedIn');

    const query = useQuery(
        ['DashboardReportMetadata', companyId, user.id, portal],
        async () => {
            const api = new backendServices.Apis.DashboardReportMetadataApi();

            const params: backendServices.Apis.GetDashboardReportMetadataParams = {
                includeDisabled: false,
                buyerCompanyId: companyId,
                userId: user.id,
                portal,
            };

            const res = await api.getDashboardReportMetadata(params);

            // parse submenu and graph configuration from xml to proper types
            return res.data
                .map((report) => {
                    try {
                        return {
                            ...report,
                            SubMenu: report.SubMenu ?? DashboardSubMenu.None,
                            GraphConfiguration: parseGraphConfiguration(
                                report.GraphConfiguration as unknown as backendServices.ViewModels.GraphConfigurationRawViewModel
                            ),
                        };
                    } catch (error) {
                        console.warn(`Failed to read dashboard report: ${report.Name}:`, error);
                        return null;
                    }
                })
                .filter((report): report is NonNullable<typeof report> => report !== null);
        },
        {
            enabled,
            staleTime: Infinity,
            cacheTime: Infinity,
        }
    );

    return query;
};

export const getStructuredDashboardReportMetadata = (
    dashboardReportMetadata: backendServices.ViewModels.DashboardReportMetadataViewModel[]
): StructuredDashboardReportMetadata => {
    // Group the metadata by Menu and SubMenu
    const structuredData = dashboardReportMetadata.reduce((acc, report) => {
        const menu = report.Menu;
        const subMenu = report.SubMenu || 'Default';

        if (!acc[menu]) {
            acc[menu] = {};
        }

        if (!acc[menu][subMenu]) {
            acc[menu][subMenu] = [];
        }

        acc[menu][subMenu].push(report);

        return acc;
    }, {} as StructuredDashboardReportMetadata);

    // Sort the menus according to the DashboardMenu enum order
    const sortedData: StructuredDashboardReportMetadata = {};
    dashboardMenuOrder.forEach((menu) => {
        if (structuredData[menu]) {
            sortedData[menu] = structuredData[menu];
        }
    });

    // Sort the list of metadata by the Order property within each submenu
    Object.keys(sortedData).forEach((menu) => {
        const menuKey = Number(menu) as DashboardMenu;
        Object.keys(sortedData[menuKey]).forEach((subMenu) => {
            sortedData[menuKey][subMenu].sort((a, b) => a.Order - b.Order);
        });
    });

    return sortedData;
};

/**
 * Meant to be used with LeftNav and Header Container
 */
export function useSortedDashboardMenus({ enabled, portal }: { enabled: boolean; portal?: DashboardPortalType }) {
    const isDashboardEnabled = useUserHasPermission('RUN_DASHBOARD_REPORT');

    const isQueryEnabled = isDashboardEnabled && enabled;

    const {
        data: dashboardMetadata,
        isLoading,
        isFetching,
    } = useGetDashboardReportMetadata({
        enabled: isQueryEnabled,
        portal,
    });

    const isLoadingDashboardMetadata = isLoading || isFetching;

    const dashboardMenus = useMemo(() => {
        if (!isQueryEnabled) {
            return undefined;
        }

        if (isLoadingDashboardMetadata || !dashboardMetadata) {
            return [];
        }

        const structuredDashboardReportMetadata = getStructuredDashboardReportMetadata(dashboardMetadata);

        // Filter dashboardMenuOrder to only include menus that have data, preserving the defined order
        const menus = dashboardMenuOrder.filter((menu) => structuredDashboardReportMetadata[menu] !== undefined);
        return menus;
    }, [dashboardMetadata, isLoadingDashboardMetadata, isQueryEnabled]);

    return { dashboardMenus, isLoadingDashboardMetadata };
}
